import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faListCheck, faClock, faCircleCheck, faCircleXmark, faHourglassHalf, faRotate
} from '@fortawesome/free-solid-svg-icons';

import { decideTask } from '../../utils/taskApproval';

/**
 * A self-assigned task asking for approval, in the chat between the employee
 * and the person they named (server: utils/taskApprovalChat.js).
 *
 * Drawn as a message from the employee - their side of the thread, their
 * bubble colour, a time - because that is what it is: them asking.
 *
 * The approver gets Approve and View - View opens the task drawer, which also
 * holds Reject, because a rejection needs a reason and a look at the task
 * first. The employee sees who it is waiting on. Once decided, the card says
 * how it went; the server rewrites the stored card and pushes it to both sides
 * as message:edited, so `decided` here only bridges the moment until then.
 */

const ids = (v) => String(v?._id || v || '');

const clockTime = (iso) =>
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const dueText = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `Due ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, `
        + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const TaskApprovalCard = ({ id, message, myId, onView }) => {
    const event = message.systemEvent;
    const task = event.task || {};
    const [decided, setDecided] = useState(null);
    const [busy, setBusy] = useState(false);

    const approverId = ids(event.targets?.[0]);
    const iApprove = approverId === String(myId);
    const iAsked = ids(event.actor) === String(myId);
    const status = decided?.decision || task.approvalStatus || 'Pending';
    const firstName = (task.employeeName || '').split(' ')[0] || 'them';

    const approve = async () => {
        setBusy(true);
        const done = await decideTask(ids(task.taskId), 'Approved', firstName);
        setBusy(false);
        if (done) setDecided(done);
    };

    const view = () => onView?.(ids(task.taskId), iApprove && status === 'Pending', (decision) => setDecided({ decision }));

    return (
        <div id={id} className={`msgr-msg ${iAsked ? 'out' : 'in'} first-of-run`}>
        <div className={`msgr-bubble msgr-approval is-${status.toLowerCase()}`}>
            <div className="msgr-approval-head">
                <FontAwesomeIcon icon={task.resubmitted ? faRotate : faListCheck} />
                {/* Whose message it is, the side of the thread already says. */}
                <span>
                    {status === 'Pending' ? (
                        <>
                            <strong>{iApprove ? 'Approval needed' : 'Sent for approval'}</strong>
                            {task.resubmitted ? ' · resubmitted' : ' · self-assigned task'}
                        </>
                    ) : (
                        // Decided or replaced: the line below says how, so no call to act.
                        <strong>{task.resubmitted ? 'Resubmitted task' : 'Self-assigned task'}</strong>
                    )}
                </span>
            </div>

            <div className="msgr-approval-title">{task.title}</div>
            <div className="msgr-approval-meta">
                {task.dueAt && <span><FontAwesomeIcon icon={faClock} /> {dueText(task.dueAt)}</span>}
                {task.priority && <span className={`msgr-tchip prio-${task.priority.toLowerCase()}`}>{task.priority}</span>}
            </div>

            {status === 'Pending' && !iApprove && (
                <div className="msgr-approval-state">
                    <FontAwesomeIcon icon={faHourglassHalf} /> Waiting for {task.approverName || 'approval'}
                </div>
            )}
            {status === 'Approved' && (
                <div className="msgr-approval-state is-approved">
                    <FontAwesomeIcon icon={faCircleCheck} /> Approved{task.decidedByName ? ` by ${task.decidedByName}` : ''}
                </div>
            )}
            {status === 'Rejected' && (
                <div className="msgr-approval-state is-rejected">
                    <FontAwesomeIcon icon={faCircleXmark} />
                    <span>
                        Rejected{task.decidedByName ? ` by ${task.decidedByName}` : ''}
                        {(decided?.note || task.approvalNote) && <em> — {decided?.note || task.approvalNote}</em>}
                    </span>
                </div>
            )}
            {status === 'Superseded' && (
                <div className="msgr-approval-state">A newer request replaced this one.</div>
            )}

            <div className="msgr-approval-actions">
                {status === 'Pending' && iApprove && (
                    <button type="button" className="is-approve" onClick={approve} disabled={busy}>Approve</button>
                )}
                <button type="button" onClick={view}>View</button>
            </div>

            {message.createdAt && <div className="msgr-approval-time">{clockTime(message.createdAt)}</div>}
        </div>
        </div>
    );
};

export default TaskApprovalCard;
